import React, { useState, useEffect, useContext } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { AuthContext } from '../../../../context/AuthContext';
import { getComments, getTaskFiles } from '../../../../api/pmsApi';
import { getManager } from '../../../../api/managerApi';

function FilesTab({ tasks, categories, onOpenTask, isProjectMaster, canCreateTask, refreshTaskSignal }) {
  // 뷰 모드 ('all': 통합 보기, 'individual': 개별 보기)
  const [viewMode, setViewMode] = useState('all');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  // 트리의 펼침/접힘 상태 관리 (기본적으로 첫 번째 세부항목만 펼치고 나머지는 접음)
  const [collapsedNodes, setCollapsedNodes] = useState({});
  const [treeData, setTreeData] = useState([]);
  const [baseDomain, setBaseDomain] = useState('');

  const { user, token } = useContext(AuthContext);

  // 회사 도메인 정보 로드 (다운로드 경로 구성용)
  useEffect(() => {
    const loadDomain = async () => {
      try {
        if (!user?.M_ComCode) return;
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) setBaseDomain(manager.M_Domain);
      } catch (err) {
        console.error('회사 도메인 조회 실패:', err);
      }
    };
    loadDomain();
  }, [user?.M_ComCode, token]);

  const collapseAll = () => {
    const allCollapsed = {};
    treeData.forEach(task => {
      allCollapsed[task.id] = true;
    });
    // 분류도 접기
    if (categories && categories.length > 0) {
      categories.forEach(cat => {
        allCollapsed[`cat_${cat.id}`] = true;
      });
    }
    setCollapsedNodes(allCollapsed);
  };



  const toggleNode = (nodeId, e) => {
    e.stopPropagation(); // 행 클릭 이벤트 버블링 방지

    const isCurrentlyCollapsed = collapsedNodes[nodeId];
    if (isCurrentlyCollapsed) {
      const taskNode = treeData.find(n => n.id === nodeId);
      if (taskNode && !taskNode.loaded && taskNode.type === 'task') {
        const taskIdStr = nodeId.split('_')[1];
        loadTaskData(nodeId, parseInt(taskIdStr));
      }
    }

    setCollapsedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const getFileIconClass = (fileName) => {
    if (!fileName) return 'fa-file text-secondary';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fa-file-word text-primary';
      case 'xls':
      case 'xlsx': return 'fa-file-excel text-success';
      case 'ppt':
      case 'pptx': return 'fa-file-powerpoint text-warning';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fa-file-image text-success';
      case 'zip':
      case 'rar': return 'fa-file-archive text-secondary';
      default: return 'fa-file text-secondary';
    }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');

    let hours = date.getHours();
    const ampm = hours >= 12 ? '오후' : '오전';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const min = String(date.getMinutes()).padStart(2, '0');
    const sec = String(date.getSeconds()).padStart(2, '0');

    return `${y}-${m}-${d} ${ampm} ${hours}:${min}:${sec}`;
  };

  const checkIsFile = (c) => {
    if (!c) return false;
    if (c.file || c.file_path || c.file_name) return true;
    return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(c.content.trim());
  };

  const formatAuthor = (userObj, writerNameFallback) => {
    const userName = userObj?.name || writerNameFallback || '알 수 없음';
    const position = userObj?.org?.position || '';
    const deptName = userObj?.org?.dept_name || '';
    if (position && deptName) return `${userName} / ${position}(${deptName})`;
    if (position || deptName) return `${userName} / ${position}${deptName}`;
    return userName;
  };

  // 고유키(_YYYYMMDDF123456) 제거 공통 함수
  const stripUniqueKey = (name) => {
    if (!name) return name;
    return name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, '');
  };

  const loadTaskData = async (nodeId, taskId) => {
    try {
      const [commentsRes, filesRes] = await Promise.all([
        getComments(taskId, token),
        getTaskFiles(taskId, token)
      ]);

      const comments = commentsRes.ok ? commentsRes.data : [];
      const taskFiles = filesRes.ok ? filesRes.data : [];
      const folders = taskFiles.filter(f => f.is_folder).map(f => f.file_name).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      const rootComments = comments.filter(c => !c.parent_id);
      const fileRootComments = rootComments.filter(c => checkIsFile(c));

      const buildReplies = (parentId) => {
        const childReplies = comments.filter(c => c.parent_id === parentId);
        let result = [];
        childReplies.forEach(reply => {
          if (checkIsFile(reply)) {
            const fileObj = reply.file || {};
            let fileName = fileObj.originalName || fileObj.file_name || reply.file_name || reply.original_name || (reply.content ? reply.content.trim() : 'Unknown File');
            const actualFileName = fileObj.file_name || reply.file_name;
            const fallbackPath = actualFileName ? `/uploads/techni/pms/${actualFileName}` : null;
            fileName = stripUniqueKey(fileName);
            result.push({
              id: `file_${reply.id}`,
              type: 'file',
              name: fileName,
              icon: getFileIconClass(fileName),
              uploader: formatAuthor(reply.user, reply.writer_name),
              date: formatDate(reply.created_at),
              rawDate: new Date(reply.created_at).getTime(),
              size: formatSize(fileObj.file_size || reply.file_size || reply.size || 1024),
              path: fileObj.file_path || fileObj.path || reply.file_path || reply.path || fallbackPath,
              isVersion: true,
              children: buildReplies(reply.id)
            });
          } else {
            result = result.concat(buildReplies(reply.id));
          }
        });
        result.sort((a, b) => b.rawDate - a.rawDate);
        return result;
      };

      const foldersMap = {};
      folders.forEach(f => { foldersMap[f] = []; });
      const unassignedFiles = [];

      fileRootComments.forEach(c => {
        const fileObj = c.file || {};
        let fileName = fileObj.originalName || fileObj.file_name || c.file_name || c.original_name || (c.content ? c.content.trim() : 'Unknown File');
        const actualFileNameRoot = fileObj.file_name || c.file_name;
        const fallbackPathRoot = actualFileNameRoot ? `/uploads/techni/pms/${actualFileNameRoot}` : null;
        fileName = stripUniqueKey(fileName);
        const node = {
          id: `file_${c.id}`,
          type: 'file',
          name: fileName,
          icon: getFileIconClass(fileName),
          uploader: formatAuthor(c.user, c.writer_name),
          date: formatDate(c.created_at),
          rawDate: new Date(c.created_at).getTime(),
          size: formatSize(fileObj.file_size || c.file_size || c.size || 1024),
          path: fileObj.file_path || fileObj.path || c.file_path || c.path || fallbackPathRoot,
          children: buildReplies(c.id)
        };

        if (c.folder_name && folders.includes(c.folder_name)) {
          foldersMap[c.folder_name].push(node);
        } else {
          unassignedFiles.push(node);
        }
      });

      const taskChildren = [];

      Object.keys(foldersMap).forEach((folderName, idx) => {
        const sortedChildren = foldersMap[folderName].sort((a, b) => b.rawDate - a.rawDate);
        taskChildren.push({
          id: `folder_${taskId}_${idx}`,
          type: 'folder',
          name: folderName,
          children: sortedChildren
        });
      });

      unassignedFiles.sort((a, b) => b.rawDate - a.rawDate);
      unassignedFiles.forEach(node => {
        taskChildren.push(node);
      });

      setTreeData(prev => prev.map(node => {
        if (node.id === nodeId) {
          return { ...node, children: taskChildren, loaded: true };
        }
        return node;
      }));

    } catch (error) {
      console.error(`Task ${taskId} 첨부파일 조회 실패:`, error);
      setTreeData(prev => prev.map(node => {
        if (node.id === nodeId) {
          return { ...node, children: [], loaded: true };
        }
        return node;
      }));
    }
  };

  // refreshTaskSignal 감지: 특정 태스크의 데이터가 변경되었다면 조용히 재조회
  useEffect(() => {
    if (refreshTaskSignal && refreshTaskSignal.taskId) {
      const nodeId = `task_${refreshTaskSignal.taskId}`;
      setTreeData(prev => {
        const node = prev.find(n => n.id === nodeId);
        if (node && node.loaded) {
          // 상태 업데이트 사이클이 끝난 후 비동기로 백그라운드 재조회 호출
          setTimeout(() => loadTaskData(nodeId, refreshTaskSignal.taskId), 0);
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTaskSignal]);

  // 빠른 지연 로딩 (Lazy Loading): 처음에는 껍데기만 세팅하고 클릭 시(toggle) 데이터 로드
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;

    // 1. 초기 트리 세팅 (백엔드에서 계산된 has_attachments 속성 활용)
    const initialTree = tasks.map(task => ({
      id: "task_" + task.id,
      type: 'task',
      name: task.title,
      originalTask: task,
      children: [],
      loaded: false
    }));

    setTreeData(initialTree);

    const validCats = categories ? [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).filter(cat => {
      const catTasks = tasks.filter(t => String(t.category_id) === String(cat.id));
      const forceDisable = catTasks.length === 0;
      return !forceDisable;
    }) : [];

    const firstValidCatId = validCats.length > 0 ? validCats[0].id : null;

    const newCollapsedNodes = {};

    // 1. 카테고리 초기화: 권한 있는 첫 번째 카테고리만 펼치고, 나머지는 접음
    categories && categories.forEach(cat => {
      if (String(cat.id) === String(firstValidCatId)) {
        newCollapsedNodes[`cat_${cat.id}`] = false;
      } else {
        newCollapsedNodes[`cat_${cat.id}`] = true;
      }
    });

    // 2. 세부항목 초기화: 펼쳐진 첫 번째 카테고리에 속한 '첫 번째' 세부항목만 펼치고 파일 로드, 나머지는 접음
    let isFirstInFirstCat = true;
    tasks.forEach(task => {
      if (String(task.category_id) === String(firstValidCatId)) {
        if (isFirstInFirstCat) {
          newCollapsedNodes[`task_${task.id}`] = false;
          loadTaskData(`task_${task.id}`, task.id);
          isFirstInFirstCat = false;
        } else {
          newCollapsedNodes[`task_${task.id}`] = true;
        }
      } else {
        newCollapsedNodes[`task_${task.id}`] = true;
      }
    });

    setCollapsedNodes(newCollapsedNodes);

    // 개별 보기용 초기 분류 선택 (권한이 있는 첫 번째 분류)
    if (firstValidCatId) {
      setSelectedCategoryId(firstValidCatId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, categories, isProjectMaster, user]);

  // 개별 보기 모드일 때 선택된 분류의 첫 번째 세부항목(Task) 데이터만 로드 및 자동 펼침
  useEffect(() => {
    if (viewMode === 'individual' && selectedCategoryId && treeData.length > 0) {
      let needsStateUpdate = false;
      const newCollapsed = { ...collapsedNodes };
      let isFirstInCat = true;

      treeData.forEach(node => {
        if (node.type === 'task' && node.originalTask && String(node.originalTask.category_id) === String(selectedCategoryId)) {
          if (isFirstInCat) {
            // 1. 첫 번째 항목만 첨부파일 데이터 로드 트리거
            if (!node.loaded) {
              const taskIdStr = node.id.split('_')[1];
              loadTaskData(node.id, parseInt(taskIdStr));
            }
            // 2. 첫 번째 항목만 강제 펼침 처리
            if (newCollapsed[node.id] !== false) {
              newCollapsed[node.id] = false;
              needsStateUpdate = true;
            }
            isFirstInCat = false;
          } else {
            // 나머지는 무조건 접음
            if (newCollapsed[node.id] !== true) {
              newCollapsed[node.id] = true;
              needsStateUpdate = true;
            }
          }
        }
      });

      if (needsStateUpdate) {
        setCollapsedNodes(newCollapsed);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedCategoryId, treeData.length]);

  // 파일명 특수문자 정제 (전각 문자 치환)
  const sanitizeFileName = (name) => {
    if (!name) return 'download';
    return name
      .replace(/</g, '＜')
      .replace(/>/g, '＞')
      .replace(/:/g, '：')
      .replace(/"/g, '＂')
      .replace(/\//g, '／')
      .replace(/\\/g, '＼')
      .replace(/\|/g, '｜')
      .replace(/\?/g, '？')
      .replace(/\*/g, '＊');
  };

  // 개별 파일 다운로드
  const handleDownloadFile = async (e, node) => {
    e.stopPropagation();
    if (!node.path) return alert('다운로드할 파일 정보가 없습니다.');
    try {
      let url = node.path.replace(/\\/g, '/');
      if (!url.startsWith('http') && !url.startsWith('/')) url = '/' + url;
      url = encodeURI(url);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      saveAs(blob, node.name);
    } catch (err) {
      console.error('개별 파일 다운로드 실패:', err);
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  // ZIP 파일 다운로드
  const handleDownloadZip = async (e, node) => {
    e.stopPropagation();
    if (!baseDomain) return alert('도메인 정보가 없어 다운로드할 수 없습니다.');

    const zip = new JSZip();
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const h = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    const dateStr = `${y}${m}${d}${h}${min}${s}`;

    const safeTitle = sanitizeFileName(node.name);
    const zipName = `${safeTitle}_(${dateStr}).zip`;

    alert(`'${node.name}' 내부의 파일들을 압축 중입니다. 잠시만 기다려 주세요...`);

    const promises = [];
    const collectFiles = (n, currentFolder = '') => {
      // 1. 현재 노드가 파일이면 ZIP 대상에 추가
      if (n.type === 'file' && n.path) {
        promises.push((async () => {
          try {
            let url = n.path.replace(/\\/g, '/');
            if (!url.startsWith('http') && !url.startsWith('/')) url = '/' + url;
            url = encodeURI(url);
            const response = await fetch(url);
            if (!response.ok) return; // 다운로드 불가 시 조용히 넘김
            const blob = await response.blob();

            // 중복 파일명 방지 로직
            let finalName = n.name;
            let counter = 1;
            while (zip.files[currentFolder + finalName]) {
              const extIdx = n.name.lastIndexOf('.');
              if (extIdx !== -1) {
                finalName = `${n.name.substring(0, extIdx)}(${counter})${n.name.substring(extIdx)}`;
              } else {
                finalName = `${n.name}(${counter})`;
              }
              counter++;
            }
            zip.file(currentFolder + finalName, blob);
          } catch (e) {
            console.error('ZIP 파일 수집 중 오류:', n.name, e);
          }
        })());
      }

      // 2. 자식 노드가 있으면(폴더 내부 파일이거나, 파일의 답글버전인 경우) 재귀 순회
      if (n.children && n.children.length > 0) {
        n.children.forEach(child => {
          // 클릭한 최상위가 Task이고 현재 순회중인 자식이 Folder일 때만 디렉토리명 추가
          if (n.type === 'task' && child.type === 'folder') {
            const newFolderPath = currentFolder + `${sanitizeFileName(child.name)}/`;
            zip.folder(newFolderPath); // 명시적으로 폴더 구조 생성 (빈 폴더라도 생성되도록 보장)
            collectFiles(child, newFolderPath);
          } else {
            // 그 외(폴더 안의 파일, 파일 안의 답글버전 등)는 경로 유지
            collectFiles(child, currentFolder);
          }
        });
      }
    };

    collectFiles(node);

    try {
      await Promise.all(promises);
      if (Object.keys(zip.files).length === 0) {
        return alert('압축할 첨부파일이 없습니다.');
      }
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, zipName);
    } catch (error) {
      console.error('ZIP 생성 오류:', error);
      alert('파일 압축 중 오류가 발생했습니다.');
    }
  };

  // 특정 노드 아래에 실제 파일이 하나라도 존재하는지 재귀적으로 검사
  const checkHasFiles = (node) => {
    if (node.type === 'file') return true;
    if (node.children && node.children.length > 0) {
      return node.children.some(child => checkHasFiles(child));
    }
    return false;
  };

  // 재귀적으로 트리를 렌더링하는 함수 (무한 뎁스 지원)
  const renderTreeNode = (node, depth = 0) => {
    const isExpanded = collapsedNodes[node.id] !== true;
    const hasChildren = node.children && node.children.length > 0;
    const isTask = node.type === 'task';
    const isFolder = node.type === 'folder';
    const isFile = node.type === 'file';


    // [순수 참조자(isPureWatcher) 판별]
    let isPureWatcher = false;
    if (isTask && node.originalTask) {
      const taskMembers = node.originalTask.members || [];
      const assignees = taskMembers.filter(m => m.is_manager === true || m.is_manager === 1);
      const isAssignee = assignees.some(a =>
        String(a.user_id) === String(user?.id) ||
        String(a.user_id) === String(user?.userid) ||
        (a.user && String(a.user.userid) === String(user?.userid)) ||
        (a.user && String(a.user.id) === String(user?.id))
      );
      isPureWatcher = !canCreateTask && !isAssignee;
    }

    // 백엔드에서 넘어온 has_attachments 활용 (로드가 아직 안 되었더라도 첨부가 있으면 화살표를 그림)
    const taskHasAttachments = isTask && node.originalTask && node.originalTask.has_attachments;
    const canToggle = hasChildren || (isTask && (!node.loaded && taskHasAttachments));

    return (
      <React.Fragment key={node.id}>
        {/* 현재 노드 행 */}
        <tr
          className={`cursor-pointer hover-row ${isTask ? 'bg-light task-row' : 'bg-white file-row'}`}
          style={{ transition: 'background-color 0.15s', cursor: isTask && isPureWatcher ? 'default' : 'pointer' }}
          onClick={(e) => {
            // 순수 참조자인 경우 태스크 행 클릭 시 접기/펼치기(toggleNode)만 허용할지, 아니면 아예 반응이 없게 할지.
            // 요구사항: "상세항목을 클릭할수없어야한다.. 마우스오버시 밑줄과 pointer가 생기면 안된다."
            // => 텍스트의 클릭만 막고 토글 꺾쇠는 클릭되게 놔둡니다.
            if (canToggle) toggleNode(node.id, e);
          }}
        >
          {/* 이름 열 (들여쓰기 적용) */}
          <td className="ps-2 pe-4 py-2 border-bottom border-light">
            <div className="d-flex align-items-center" style={{ paddingLeft: `${depth * 28}px` }}>

              {/* 폴더/태스크 열기 꺾쇠 아이콘 */}
              <div style={{ width: '20px', textAlign: 'center' }}>
                {canToggle ? (
                  <i className={`fas fa-caret-${isExpanded ? 'down' : 'right'} text-secondary`} style={{ fontSize: '0.85rem' }}></i>
                ) : (
                  <div style={{ width: '20px' }}></div> // 자식이 없으면 꺾쇠 자리만 차지함
                )}
              </div>

              {/* 타입별 아이콘 및 뱃지 */}
              {isTask && <span className="badge ms-1 me-2 px-2 py-1 shadow-sm" style={{ backgroundColor: '#e7f5ff', color: '#1c7ed6', fontSize: '0.65rem', borderRadius: '4px' }}>세부항목</span>}
              {isFolder && <i className={`fas ${isExpanded ? 'fa-folder-open' : 'fa-folder'} text-warning ms-1 me-2`} style={{ fontSize: '1rem' }}></i>}
              {isFile && !node.isVersion && <i className={`fas ${node.icon} ms-1 me-2`} style={{ fontSize: '1.1rem' }}></i>}
              {/* 자식 버전 파일(↳) 표시 */}
              {isFile && node.isVersion && (
                <>
                  <i className="fas fa-level-up-alt fa-rotate-90 text-muted ms-1 me-2" style={{ fontSize: '0.8rem' }}></i>
                  <i className={`fas ${node.icon} me-2`} style={{ fontSize: '1rem' }}></i>
                </>
              )}

              {/* 노드 이름 */}
              {isFile ? (
                <span
                  className="text-truncate file-name-text cursor-pointer"
                  style={{ color: '#6c757d', fontSize: '0.85rem', fontWeight: '500', flex: 1, minWidth: 0 }}
                  title={node.name}
                  onClick={(e) => handleDownloadFile(e, node)}
                >
                  {node.name}
                </span>
              ) : (
                <span
                  className={`text-truncate ${isTask ? 'fw-bold' : ''} ${isTask && onOpenTask && !isPureWatcher ? 'task-name-hover' : ''}`}
                  style={{
                    color: isTask ? '#343a40' : '#495057',
                    fontSize: isTask ? '0.9rem' : '0.85rem',
                    flex: 1, minWidth: 0,
                    transition: 'color 0.15s',
                    cursor: (isTask && onOpenTask && !isPureWatcher) ? 'pointer' : 'default'
                  }}
                  title={node.name}
                  onClick={(e) => {
                    if (isTask && onOpenTask && node.originalTask && !isPureWatcher) {
                      e.stopPropagation();
                      onOpenTask(node.originalTask, 'files');
                    }
                  }}
                >
                  {node.name}
                </span>
              )}
            </div>
            <style>{`
              .task-name-hover:hover { color: #4c6ef5 !important; }
              .hover-row .download-btn-wrapper { opacity: 0; transition: opacity 0.2s; }
              .hover-row:hover .download-btn-wrapper { opacity: 1; }
            `}</style>
          </td>

          {/* 작성자, 등록일, 크기 열 */}
          <td className="py-2 border-bottom border-light text-secondary text-center" style={{ fontSize: '0.8rem' }}>{node.uploader || ''}</td>
          <td className="py-2 border-bottom border-light text-secondary text-center" style={{ fontSize: '0.8rem' }}>{node.date || ''}</td>
          <td className="py-2 border-bottom border-light text-secondary fw-medium text-center" style={{ fontSize: '0.8rem' }}>{node.size || ''}</td>

          {/* 다운로드 버튼 열 */}
          <td className="py-2 border-bottom border-light text-center px-4">
            <div className="download-btn-wrapper">
              {isFile && (
                <button
                  className="btn btn-light rounded-circle shadow-sm icon-btn d-inline-flex align-items-center justify-content-center"
                  title="단일 파일 다운로드"
                  onClick={(e) => handleDownloadFile(e, node)}
                  style={{ width: '26px', height: '26px', padding: '0', border: '1px solid #dee2e6' }}
                >
                  <i className="fas fa-download text-secondary" style={{ fontSize: '0.75rem' }}></i>
                </button>
              )}
              {(isTask || isFolder) && (() => {
                const hasFiles = checkHasFiles(node);
                if (!hasFiles) return null;
                return (
                  <button
                    className="btn btn-light rounded-circle shadow-sm icon-btn zip-btn d-inline-flex align-items-center justify-content-center"
                    title="선택 영역 ZIP 압축 다운로드"
                    onClick={(e) => {
                      handleDownloadZip(e, node);
                    }}
                    style={{
                      width: '26px', height: '26px', padding: '0', border: '1px solid #dee2e6',
                      opacity: 1,
                      cursor: 'pointer'
                    }}
                  >
                    <span className="text-secondary fw-bold" style={{ fontSize: '0.55rem', letterSpacing: '0.5px' }}>ZIP</span>
                  </button>
                );
              })()}
            </div>
          </td>
        </tr>

        {/* 자식 노드 렌더링 (재귀 호출) */}
        {isExpanded && hasChildren && node.children.map(child => renderTreeNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  const handleViewModeChange = (mode) => {
    if (viewMode === mode) return;
    setViewMode(mode);

    const validCats = categories ? [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).filter(cat => {
      const catTasks = tasks.filter(t => String(t.category_id) === String(cat.id));
      return catTasks.length > 0;
    }) : [];

    const firstValidCatId = validCats.length > 0 ? validCats[0].id : null;

    const newCollapsedNodes = {};
    categories && categories.forEach(cat => {
      if (String(cat.id) === String(firstValidCatId)) {
        newCollapsedNodes[`cat_${cat.id}`] = false;
      } else {
        newCollapsedNodes[`cat_${cat.id}`] = true;
      }
    });

    let isFirstInViewChange = true;
    tasks.forEach(task => {
      if (String(task.category_id) === String(firstValidCatId)) {
        if (isFirstInViewChange) {
          newCollapsedNodes[`task_${task.id}`] = false;
          loadTaskData(`task_${task.id}`, task.id);
          isFirstInViewChange = false;
        } else {
          newCollapsedNodes[`task_${task.id}`] = true;
        }
      } else {
        newCollapsedNodes[`task_${task.id}`] = true;
      }
    });

    setCollapsedNodes(newCollapsedNodes);

    if (firstValidCatId) {
      setSelectedCategoryId(firstValidCatId);
    }
  };

  const isTreeEmpty = treeData.length === 0;
  const individualCatTasks = viewMode === 'individual' ? treeData.filter(n => n.originalTask && String(n.originalTask.category_id) === String(selectedCategoryId)) : [];
  const firstCatTask = individualCatTasks.length > 0 ? individualCatTasks[0] : null;
  const isLoading = viewMode === 'individual' && firstCatTask && !firstCatTask.loaded;
  const isTargetEmpty = viewMode === 'individual' && individualCatTasks.length === 0;
  const showEmptyState = isTreeEmpty || isTargetEmpty;

  return (
    <div className="d-flex flex-column h-100 animation-fade-in">

      {/* 🔹 상단 컨트롤 영역 (타이틀 삭제, 토글/콤보박스 배치) */}
      <div className="d-flex justify-content-between align-items-center mb-1 px-2 mt-1">
        {/* 좌측: 뷰 모드 전환 (라디오 토글 디자인) */}
        <div className="btn-group" role="group">
          <input type="radio" className="btn-check" id="viewModeAll" checked={viewMode === 'all'} onChange={() => handleViewModeChange('all')} />
          <label className="btn btn-custom-toggle px-3 fw-bold" htmlFor="viewModeAll">
            <i className="fas fa-list-ul me-1"></i> 통합 보기
          </label>

          <input type="radio" className="btn-check" id="viewModeInd" checked={viewMode === 'individual'} onChange={() => handleViewModeChange('individual')} />
          <label className="btn btn-custom-toggle px-3 fw-bold" htmlFor="viewModeInd">
            <i className="fas fa-file-alt me-1"></i> 개별 보기
          </label>
        </div>

        {/* 우측: 상황별 컨트롤 (전체 접기 or 콤보박스) */}
        <div className="d-flex align-items-center">
          {viewMode === 'all' ? (
            treeData.length > 0 ? (
              <button className="btn shadow-sm action-btn py-1" style={{ fontSize: '0.85rem' }} onClick={collapseAll}>
                <i className="fas fa-angle-double-up me-1"></i>전체 접기
              </button>
            ) : null
          ) : (
            <div className="d-flex align-items-center">
              <span className="fw-bold text-secondary me-2">분류 :</span>
              <select
                className="form-select shadow-sm fw-bold text-dark"
                style={{ width: '400px', borderColor: '#ced4da' }}
                value={selectedCategoryId || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCategoryId(val);
                }}
              >
                {categories && categories.length > 0 ? (
                  [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(cat => {
                    const catTasks = treeData.filter(n => n.originalTask && String(n.originalTask.category_id) === String(cat.id));
                    const forceCollapseEmpty = catTasks.length === 0;

                    return (
                      <option key={cat.id} value={cat.id} disabled={forceCollapseEmpty}>
                        {cat.name} {forceCollapseEmpty ? '(세부항목 없음)' : ''}
                      </option>
                    );
                  })
                ) : (
                  <option value="">등록된 분류가 없습니다</option>
                )}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* 🔹 트리형 데이터 테이블 (재귀 렌더링) */}
      <div className="bg-white rounded-3 shadow-sm border flex-grow-1 overflow-auto p-0 custom-scrollbar d-flex flex-column">
        <table className="table table-hover align-middle mb-0" style={{ tableLayout: 'fixed' }}>
          <thead className="sticky-top" style={{ zIndex: 10 }}>
            <tr style={{ height: '42px', backgroundColor: '#e9ecef' }}>
              <th className="ps-2 pe-4 text-dark fw-bolder align-middle" style={{ width: '45%', borderBottom: '2px solid #ced4da', fontSize: '0.85rem', backgroundColor: '#e9ecef' }}>이름</th>
              <th className="text-dark fw-bolder align-middle text-center" style={{ width: '15%', borderBottom: '2px solid #ced4da', fontSize: '0.85rem', backgroundColor: '#e9ecef' }}>작성자</th>
              <th className="text-dark fw-bolder align-middle text-center" style={{ width: '15%', borderBottom: '2px solid #ced4da', fontSize: '0.85rem', backgroundColor: '#e9ecef' }}>등록일</th>
              <th className="text-dark fw-bolder align-middle text-center" style={{ width: '12%', borderBottom: '2px solid #ced4da', fontSize: '0.85rem', backgroundColor: '#e9ecef' }}>크기</th>
              <th className="text-dark fw-bolder align-middle text-center" style={{ width: '13%', borderBottom: '2px solid #ced4da', fontSize: '0.85rem', backgroundColor: '#e9ecef' }}>다운로드</th>
            </tr>
          </thead>
          {!showEmptyState && (
            <tbody className="border-top-0">
              {viewMode === 'all' ? (
                categories && categories.length > 0 ? (
                  (() => {
                    return [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(cat => {
                      const catNodeId = `cat_${cat.id}`;
                      const catTasks = treeData.filter(n => n.originalTask && String(n.originalTask.category_id) === String(cat.id));
                      const forceCollapseEmpty = catTasks.length === 0;
                      const isCatExpanded = forceCollapseEmpty ? false : (collapsedNodes[catNodeId] !== true);

                      return (
                        <React.Fragment key={catNodeId}>
                          <tr
                            style={{
                              cursor: forceCollapseEmpty ? 'default' : 'pointer',
                              backgroundColor: '#f4f5f7',
                              opacity: forceCollapseEmpty ? 0.6 : 1
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!forceCollapseEmpty) {
                                const isOpening = collapsedNodes[catNodeId] === true || collapsedNodes[catNodeId] === undefined;

                                setCollapsedNodes(prev => {
                                  const nextState = { ...prev, [catNodeId]: !isOpening };
                                  if (isOpening && catTasks.length > 0) {
                                    const firstTask = catTasks[0];
                                    const taskId = firstTask.originalTask.id;
                                    nextState[`task_${taskId}`] = false;
                                    if (!firstTask.loaded) {
                                      loadTaskData(`task_${taskId}`, taskId);
                                    }
                                  }
                                  return nextState;
                                });
                              }
                            }}
                          >
                            <td colSpan={5} className="ps-2 py-2 border-bottom border-light fw-bold" style={{ fontSize: '0.9rem', color: '#343a40' }}>
                              <div className="d-flex align-items-center">
                                <div style={{ width: '20px', textAlign: 'center', opacity: forceCollapseEmpty ? 0.5 : 1 }}>
                                  <i className={`fas fa-caret-${isCatExpanded ? 'down' : 'right'} text-secondary`} style={{ fontSize: '0.85rem' }}></i>
                                </div>
                                <i className="fas fa-layer-group text-primary ms-1 me-2" style={{ fontSize: '0.85rem', opacity: forceCollapseEmpty ? 0.5 : 1 }}></i>
                                <span style={{ textDecoration: 'none' }}>{cat.name}</span>
                                <span className="badge bg-light text-secondary ms-2 border px-2 py-1 fs-8" style={{ opacity: forceCollapseEmpty ? 0.5 : 1 }}>
                                  {catTasks.length}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {isCatExpanded && catTasks.map(taskNode => renderTreeNode(taskNode, 1))}
                        </React.Fragment>
                      );
                    });
                  })()
                ) : (
                  treeData.map(taskNode => renderTreeNode(taskNode, 0))
                )
              ) : (
                (() => {
                  const cat = categories?.find(c => String(c.id) === String(selectedCategoryId));
                  if (!cat) return null;
                  const catTasks = treeData.filter(n => n.originalTask && String(n.originalTask.category_id) === String(cat.id));
                  return (
                    <React.Fragment>
                      {catTasks.map(taskNode => renderTreeNode(taskNode, 0))}
                    </React.Fragment>
                  );
                })()
              )}
            </tbody>
          )}
        </table>

        {(showEmptyState || isLoading) && (
          <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-muted" style={{ minHeight: '300px' }}>
            {isLoading ? (
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <div className="mb-2">
                <i className="fas fa-folder-open" style={{ fontSize: '2.5rem', color: '#dee2e6' }}></i>
              </div>
            )}
            <span className="fw-medium" style={{ fontSize: '0.85rem' }}>
              {isLoading ? '데이터를 불러오는 중입니다...' : (isTreeEmpty ? '등록된 세부항목이 없습니다.' : '첨부파일이 존재하지 않습니다.')}
            </span>
          </div>
        )}
      </div>

      {/* 🔹 CSS 스타일 */}
      <style>{`
        /* 테이블 콤팩트 뷰 및 가로선 아주 흐리게 처리 */
        .table tbody tr { height: 38px !important; }
        .table tbody tr.task-row { height: 44px !important; }
        .table tbody td { 
          padding-top: 4px !important; 
          padding-bottom: 4px !important; 
          border-bottom: 1px solid rgba(0, 0, 0, 0.03) !important; 
        }

        /* 라디오 토글 커스텀 색상 (슬레이트 블루) */
        .btn-custom-toggle { color: #495057; border: 1px solid #ced4da; background-color: #fff; }
        .btn-custom-toggle:hover { color: #4c6ef5 !important; border-color: #4c6ef5 !important; background-color: #fff !important; }
        .btn-check:checked + .btn-custom-toggle { background-color: #4c6ef5 !important; border-color: #4c6ef5 !important; color: #fff !important; }
        .btn-check:checked + .btn-custom-toggle:hover { background-color: #3b5bdb !important; border-color: #3b5bdb !important; color: #fff !important; }
        
        /* 테이블 및 가로선 스타일 */
        .table tbody td { border-bottom: 1px solid #f1f3f5 !important; }
        .table-hover tbody tr:not(.no-hover-row):hover { background-color: #f8f9fa !important; }
        .table-hover tbody tr.no-hover-row:hover { background-color: #fff !important; cursor: default; }
        .file-row:hover .file-name-text { color: #4c6ef5 !important; text-decoration: underline; }
        
        /* 상단 액션 버튼 (전체 접기 등) 호버 효과 */
        .action-btn { background-color: #fff; border: 1px solid #ced4da; color: #6c757d; transition: all 0.2s ease; }
        .action-btn:hover { background-color: #f8f9fa; color: #4c6ef5; border-color: #4c6ef5; }

        /* 아이콘 버튼 효과 */
        .icon-btn { transition: all 0.2s ease; }
        .icon-btn:hover { background-color: #4c6ef5 !important; border-color: #4c6ef5 !important; }
        .icon-btn:hover i, .icon-btn:hover span { color: #fff !important; }
        
        /* ZIP 전용 호버 색상 (단일 다운로드와 구분되게 진회색) */
        .zip-btn:hover { background-color: #6c757d !important; border-color: #6c757d !important; }
        
        /* 기타 유틸 */
        .cursor-pointer { cursor: pointer; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #dee2e6; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #adb5bd; }
      `}</style>
    </div>
  );
}

export default FilesTab;
